import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PostUser, PutUser, User, UserPersonalization } from 'src/app/interfaces/api/user';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../core/api.service';
import { AuthService } from '../../core/auth.service';

type QueryParams = Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
  providedIn: 'root',
})

export class UserService {

  collection = "User";
  collectionPath = "users";
  users: User[] = [];
  apiUrl = environment.API_URL;
  user: User;

  constructor(
    public http: HttpClient,
    private apiService: ApiService,
    private authService: AuthService
  ) {
    this.user = this.authService.currentUserValue;
  }

  //---GET---//

  getUsers(query: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, "", query);
  }

  getUser(id: string): Observable<any> {
    return this.apiService.getCollectionItem(this.collectionPath, "", id + '?full=true');
  }

  searchAndCountUsers(search: string, query: QueryParams = {}): Observable<{ results: User[], total: number }> {
    return this.apiService.getCollection(this.collectionPath, 'searchAndCount', { q: search, ...query });
  }

  searchUsers(search: string, query: QueryParams = {}): Observable<User[]> {
    return this.apiService.getCollection(this.collectionPath, 'search', { q: search, ...query });
  }

  getUsersCount(search: string = "", query: QueryParams): Observable<number> {
    return this.apiService.getCollection(this.collectionPath, 'count', { q: search, ...query });
  }

  getUserById(id: string): Observable<User> {
    const headers: HttpHeaders = new HttpHeaders({
      Authorization: `Bearer ${localStorage.getItem('authToken')}`, // Ajoute le token ici
    });

    return this.apiService.getCollectionItem("users", "", id, { headers });
  }

  getUsersLight(projectId: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'light', { projects: projectId });
  }

  reloadUser() {
    const currentUser = this.authService.currentUser();

    if (currentUser)
      return this.apiService.getCollectionItem(this.collectionPath, "", currentUser.id)
        .subscribe((result: any) => {
          if (result) {
            let savedUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;
            localStorage.setItem('user', JSON.stringify({ ...result, lastConnection: savedUser?.lastConnection }));
            this.authService.setUser(result);
          }

          return result;
        });
    else
      return null;
  }

  //---POST---//

  verifyUserByPassword(element: any): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'verifyPassword', { email: element.email, password: element.password });
  }

  addUser(user: PostUser): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'signUp', user);
  }

  addGuestUser(user: PostUser): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'addGuest', user);
  }

  resendInvitation(userId: string): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'resend-invitation', { userId });
  }

  //---PUT---//

  setUser(user: PutUser): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, "", user);
  }

  setCurrentProject(projectId: string): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'project', { project: projectId });
  }

  setSelfRole(role: string): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'self-role', { role });
  }

  updateLastPosition(newPosition: any, viewerPositions: any[]) {
    this.user = this.authService.currentUser();
    let lastPosition = [];
    lastPosition = viewerPositions;
    const lastPositionIndex = lastPosition.findIndex(t => t.projectId == newPosition.projectId);
    if (lastPositionIndex > -1) lastPosition.splice(lastPositionIndex, 1);
    lastPosition.push(newPosition);

    const query = { id: this.user.id, email: this.user.email, viewerPositions: lastPosition };

    this.user.viewerPositions = lastPosition;
    this.authService.setUser(this.user);
    this.apiService.updateCollection(this.collectionPath, "", query).subscribe({
      next: async res => {
        this.authService.currentUser()
      },
      error: err => {
        console.error(err);
      }
    });
  }

  updateFavouriteProjects(favourites: string[]) {
    this.user = this.authService.currentUser();
    const query = { id: this.user.id, email: this.user.email, favouriteProjects: favourites };

    this.apiService.updateCollection(this.collectionPath, "", query).subscribe({
      next: res => {
        this.authService.updateFavouriteProjects(favourites);
      },
      error: err => {
        console.error(err);
      }
    });
  }

  updatePersonalization(personalization: Partial<UserPersonalization>): Observable<any> {
    this.user = this.authService.currentUser();
    const settings = { ...this.user.settings, ...personalization };
    const query = { id: this.user.id, email: this.user.email, settings };

    return this.apiService.updateCollection(this.collectionPath, "", query).pipe(
      tap(() => this.authService.updateSettings(settings))
    );
  }

  addVisitedProjects(projects: any) {
    this.user = this.authService.currentUser();
    const query = { id: this.user.id, email: this.user.email, projectsVisited: projects };

    this.apiService.updateCollection(this.collectionPath, "", query).subscribe({
      next: res => {
        this.authService.updateProjectsVisited(projects);
      },
      error: err => {
        console.error(err);
      }
    });
  }

  setLanguage(lang: string) {
    let user: User = this.authService.currentUser();

    if (user) {
      const query = { id: user.id, email: user.email, language: lang };

      // this.http.put(this.API_URL + 'users', query).subscribe(async res => {
      this.apiService.updateCollection(this.collectionPath, "", query).subscribe({
        next: res => {
          user.language = query.language;
          this.setUser(user);
        },
        error: err => {
          console.error(err);
          user.language = query.language;
          this.setUser(user);
        },
      });
    }
  }

  getSecuritySettings(): Observable<{ mfaEnabled: boolean; mfaMethod?: 'email' | 'totp' }> {
    return this.apiService.getCollection('account', 'security');
  }

  startMfaEnrollment(method: 'email' | 'totp'): Observable<{ enrollToken: string; mfaMethod: string; expiresAt: string; qrCodeUri?: string }> {
    return this.apiService.postCollectionData('account', 'security/mfa/enrollment', { method });
  }

  confirmMfaEnrollment(enrollToken: string, code: string): Observable<any> {
    return this.apiService.postCollectionData('account', 'security/mfa/enrollment/confirm', { enrollToken, code });
  }

  startMfaDisable(): Observable<{ disableToken: string; mfaMethod: string; expiresAt: string }> {
    return this.apiService.postCollectionData('account', 'security/mfa/disable', {});
  }

  confirmMfaDisable(disableToken: string, code: string): Observable<any> {
    return this.apiService.postCollectionData('account', 'security/mfa/disable/confirm', { disableToken, code });
  }

  //---DELETE---//

  removeUser(id: string): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath, id);
  }

  //---OTHER---//

  _initLanguage() {
    // recovery of the language of the device used
    if (window.Intl && typeof window.Intl === 'object') { return navigator.language }
    else {
      return 'en-US'
    }
  }

  initLanguage() {
    // recovery of the language of the device used
    if (window.Intl && typeof window.Intl === 'object') {
      if (navigator.language == 'en-US' || navigator.language == 'en') return 'en-US';
      if (navigator.language == 'fr-FR' || navigator.language == 'fr') return 'fr-FR';

      return 'en-US';

    }
    else {
      return 'en-US';
    }
  }

}
